<template lang="pug">
web-app-layout(
  :showLogo="true"
  :noPadding="true"
  :hasGradient="showGradient"
  :whiteMode="showGradient"
)
  // Full-width gradient background (outside container)
  .gradient-background-full(v-if="showGradient && staticPageData")
  
  .static-page-container(:class="{ 'has-gradient': showGradient && isHomePage }")
    // Loading state
    web-page-skeleton(v-if="loading" :sections="3" :cardsPerSection="6")

    // Error state
    .error-container(v-else-if="error")
      .error-content
        .error-icon(:class="`error-icon--${errorType}`")
          img(
            v-if="errorType === 'not_found'"
            src="@/assets/icons/magnify.svg"
            alt=""
          )
          span(v-else) ⚠️
        h3.error-title {{ errorTitle }}
        p.error-message {{ errorMessage }}
        .error-actions
          button.retry-button(@click="handleRetry") Try Again
          router-link.home-link(v-if="!isHomePage" to="/web/") Go to Home

    // Loaded state
    .content-container(v-else-if="staticPageData")
      .blocks-wrapper
        component(
          v-for="block in sortedBlocks"
          :key="block.id"
          :is="getBlockComponent(block.blockType)"
          :block="block"
        )
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, onMounted, watch, provide,
} from 'vue';
import WebAppLayout from '../dashboard/WebAppLayout.vue';
import WebPageSkeleton from '../components/WebPageSkeleton.vue';
import { useStaticPage } from './useStaticPage';
import { StaticPageData, BlockTypes } from './types';
import BannerBlock from './blocks/BannerBlock.vue';
import SearchBarBlock from './blocks/SearchBarBlock.vue';
import StickersBlock from './blocks/StickersBlock.vue';
import OrganizationsBlock from './blocks/OrganizationsBlock.vue';
import RichTextBlock from './blocks/RichTextBlock.vue';
import AnnouncementBlock from './blocks/AnnouncementBlock.vue';
import QuestionBlock from './blocks/QuestionBlock.vue';
import CollectionsBlock from './blocks/CollectionsBlock.vue';
import ListBlock from './blocks/ListBlock.vue';

export default defineComponent({
  name: 'StaticPage',
  components: {
    WebAppLayout,
    WebPageSkeleton,
    BannerBlock,
    SearchBarBlock,
    StickersBlock,
    OrganizationsBlock,
    RichTextBlock,
    AnnouncementBlock,
    QuestionBlock,
    CollectionsBlock,
    ListBlock,
  },
  props: {
    slug: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    const {
      getStaticPage,
      loading,
      error,
      errorMessage,
      errorTitle,
      errorType,
    } = useStaticPage();

    // State
    const staticPageData = ref<StaticPageData | null>(null);
    // Lets blocks build links back to this page (e.g. a shareable collection URL)
    provide('staticPageSlug', computed(() => props.slug));

    // Computed
    const isHomePage = computed(() => props.slug === 'home-page');

    const showGradient = computed(
      () => staticPageData.value?.greenGradientEnabled || false,
    );

    const sortedBlocks = computed(() => {
      if (!staticPageData.value?.blocks) return [];

      // Blocks are already sorted by the composable
      return staticPageData.value.blocks;
    });

    // Methods
    const fetchData = async () => {
      const data = await getStaticPage(props.slug);
      staticPageData.value = data;
    };

    const handleRetry = () => {
      fetchData();
    };

    const getBlockComponent = (blockType: string) => {
      const componentMap: { [key: string]: string } = {
        [BlockTypes.BANNER]: 'BannerBlock',
        [BlockTypes.SEARCH_BAR]: 'SearchBarBlock',
        [BlockTypes.STICKERS]: 'StickersBlock',
        [BlockTypes.ORGANIZATIONS]: 'OrganizationsBlock',
        [BlockTypes.RICH_TEXT]: 'RichTextBlock',
        [BlockTypes.ANNOUNCEMENT]: 'AnnouncementBlock',
        [BlockTypes.QUESTION]: 'QuestionBlock',
        [BlockTypes.COLLECTIONS]: 'CollectionsBlock',
        [BlockTypes.LIST]: 'ListBlock',
      };

      return componentMap[blockType] || 'div';
    };

    // Lifecycle
    onMounted(() => {
      fetchData();
    });

    // Watch for slug changes to reload content when navigating between static pages
    watch(() => props.slug, (newSlug, oldSlug) => {
      if (newSlug !== oldSlug) {
        fetchData();
      }
    });

    return {
      loading,
      error,
      errorMessage,
      errorTitle,
      errorType,
      staticPageData,
      isHomePage,
      showGradient,
      sortedBlocks,
      handleRetry,
      getBlockComponent,
    };
  },
});
</script>

<style lang="scss">
// Full-width gradient that breaks out of container
// NOTE: Not scoped so it can properly break out to full viewport width
.gradient-background-full {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  max-width: 100vw;
  margin-left: calc(50% - 50vw);
  height: 200px;
  background: linear-gradient(
    180deg,
    #225c6b 0%,
    #225c6b 5%,
    rgba(34, 92, 107, 0.6) 60%,
    rgba(34, 92, 107, 0) 100%
  );
  z-index: 0;
  pointer-events: none;

  @media (min-width: 768px) {
    height: 250px;
  }

  // Align with full-bleed header when desktop sidebar is visible
  @media (min-width: 1024px) {
    .pl-main-menu-width & {
      margin-left: calc(50% - 50vw - 150px);
    }
  }
}
</style>

<style lang="scss" scoped>
// Scoped styles for the rest of the component
.static-page-container {
  position: relative;
  min-height: 100vh;
  margin-top: 0;
  z-index: 1;
  max-width: 1200px;

  &.has-gradient {
    margin-top: 0;
  }
}

// Error State
.error-container {
  padding: 80px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;

  @media (min-width: 768px) {
    padding: 100px 40px;
  }

  @media (min-width: 1024px) {
    padding: 120px 64px;
  }
}

.error-content {
  max-width: 420px;
  text-align: center;
}

.error-icon {
  margin-bottom: 20px;

  span {
    font-size: 48px;
    line-height: 1;
    opacity: 0.7;
  }

  img {
    width: 56px;
    height: 56px;
    opacity: 0.45;
  }
}

.error-title {
  margin: 0 0 12px;
  color: #1a1a1a;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.3;
}

.error-message {
  margin: 0 0 28px;
  color: #6b7280;
  font-size: 16px;
  line-height: 1.6;
}

.error-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.retry-button {
  padding: 12px 28px;
  background: linear-gradient(90deg, #14b8a6 0%, #0d9488 100%);
  color: #ffffff;
  border: none;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, opacity 0.2s;

  &:hover {
    transform: scale(1.02);
    opacity: 0.95;
  }
}

.home-link {
  color: #0d9488;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

// Content Container
.content-container {
  position: relative;
  width: 100%;
  background: #ffffff;
}

// When gradient is present, pull content up to overlap
.static-page-container.has-gradient .content-container {
  background: transparent; // Transparent so gradient shows through
  margin-top: -300px;
  
  @media (min-width: 768px) {
    margin-top: -300px;
  }
}

.blocks-wrapper {
  position: relative;
  z-index: 1;
  padding: 32px 0 64px;
  width: 100%;

  @media (min-width: 768px) {
    padding: 40px 0 80px;
  }

  @media (min-width: 1024px) {
    padding: 48px 0 96px;
  }
}
</style>
